/**
 * Bingo — sound palette.
 *
 * Aesthetic: a warm community hall. The defining sounds:
 *   - DAUB: a soft, felt-tipped "thup" — a low filtered noise transient with a
 *     short pitched body. The sound of a sponge dauber pressed onto paper.
 *   - CALL: a warm two-note "ding" with a brass ring — the caller's chime
 *     before they read the number. This is the audio half of the three-way
 *     announcement (chime + big letter-number + readable text). It is NEVER the
 *     only signal; muting it leaves the number fully visible and spoken in text.
 *   - BINGO: a warm rising four-note flourish on a major chord with a brass
 *     shimmer — celebratory but calm, ~1.1s.
 *
 * All public play* calls route through `audio.playIfNotMuted`, so the global
 * mute toggle silences everything (including the caller) automatically. The
 * synthesis here is wholly new — no Five/Mahjong functions, different
 * frequencies, different envelopes.
 */

import { audio } from '@/juice';

import type { HapticStyle } from '@rendever/games-contracts';

export type BingoSoundName =
  | 'daub'
  | 'undaub'
  | 'call'
  | 'callLast'
  | 'tap'
  | 'nudge'
  | 'win'
  | 'pause'
  | 'resume'
  | 'whoosh';

export const soundHaptic: Partial<Record<BingoSoundName, HapticStyle>> = {
  daub: 'medium',
  undaub: 'light',
  call: 'light',
  callLast: 'medium',
  tap: 'light',
  nudge: 'light',
  win: 'success',
  pause: 'light',
  resume: 'light',
  whoosh: 'light',
};

// ---------------------------------------------------------------------------
// Helpers — all bespoke to Bingo
// ---------------------------------------------------------------------------

/**
 * The DAUB — a felt "thup". A short band-passed noise burst (the dab) over a
 * very short low pitched body (the paper). Soft, round, not a click.
 */
function synthDaub(opts: { pitch?: number; gain?: number } = {}): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const pitch = opts.pitch ?? 196; // G3 — a low, soft body
  const peak = opts.gain ?? 0.22;

  // Pitched body — a quick muffled thud.
  const osc = audio.makeOscillator(ctx, { freq: pitch, type: 'sine' });
  osc.frequency.exponentialRampToValueAtTime(pitch * 0.6, t + 0.1);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.004,
    decay: 0.04,
    sustain: 0.2,
    release: 0.1,
    peak,
  });
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 900;
  audio.connectThrough(osc, lp, g, master);
  osc.start(t);
  osc.stop(t + 0.18);

  // The dab — soft band-passed noise, the sponge meeting paper.
  const buf = audio.noiseBuffer(ctx, { lengthMs: 60, shaping: 1.6 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 540;
  bp.Q.value = 0.8;
  const { node: ng } = audio.envelope(ctx, {
    attack: 0.002,
    decay: 0.02,
    sustain: 0.1,
    release: 0.06,
    peak: peak * 0.7,
  });
  audio.connectThrough(src, bp, ng, master);
  src.start(t);
  src.stop(t + 0.1);
}

/** Un-daub — lighter, higher, "lifted off". */
function synthUndaub(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 320, type: 'sine' });
  osc.frequency.exponentialRampToValueAtTime(440, t + 0.08);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.003,
    decay: 0.02,
    sustain: 0.2,
    release: 0.08,
    peak: 0.12,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.14);
}

/**
 * The CALL chime — a warm two-note "ding-dong" with a soft brass ring. Played
 * just before the number is announced. `accent` raises it a little for the very
 * last call of a game.
 */
function synthCall(accent = false): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t0 = ctx.currentTime;
  // A friendly major-third call: E5 then up to G5 (or a brighter pair on accent).
  const notes = accent ? [659.26, 987.77] : [659.26, 783.99];
  notes.forEach((freq, i) => {
    const t = t0 + i * 0.14;
    const osc = audio.makeOscillator(ctx, { freq, type: 'triangle' });
    const { node: g } = audio.envelope(ctx, {
      attack: 0.006,
      decay: 0.1,
      sustain: 0.4,
      release: 0.5,
      peak: 0.16,
    });
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 3200;
    audio.connectThrough(osc, lp, g, master);
    osc.start(t);
    osc.stop(t + 0.6);

    // Brass ring — a quiet fifth above, sine, long tail.
    const ring = audio.makeOscillator(ctx, { freq: freq * 1.5, type: 'sine' });
    const rg = ctx.createGain();
    rg.gain.setValueAtTime(0.0001, t);
    rg.gain.exponentialRampToValueAtTime(0.05, t + 0.03);
    rg.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
    audio.connectThrough(ring, rg, master);
    ring.start(t);
    ring.stop(t + 0.75);
  });
}

/** Small UI tap — picker tiles, toggles. */
function synthTap(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 560, type: 'triangle' });
  const { node: g } = audio.envelope(ctx, {
    attack: 0.002,
    decay: 0.02,
    sustain: 0.2,
    release: 0.06,
    peak: 0.12,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.1);
}

/** Gentle nudge — a soft single chime drawing the eye to a missed call. */
function synthNudge(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 880, type: 'sine' });
  const { node: g } = audio.envelope(ctx, {
    attack: 0.01,
    decay: 0.06,
    sustain: 0.3,
    release: 0.4,
    peak: 0.1,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.5);
}

/**
 * BINGO — a warm rising four-note flourish on a major chord (C–E–G–C) with a
 * brass shimmer over the top note. ~1.1s. Celebratory but calm.
 */
function synthWin(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t0 = ctx.currentTime;
  const notes = [523.25, 659.26, 783.99, 1046.5]; // C5 E5 G5 C6
  notes.forEach((freq, i) => {
    const t = t0 + i * 0.13;
    const osc = audio.makeOscillator(ctx, { freq, type: 'triangle' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.2, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 3400;
    audio.connectThrough(osc, lp, g, master);
    osc.start(t);
    osc.stop(t + 0.95);
  });
  // Brass shimmer over the final note — an octave sparkle with a long tail.
  const shimmerAt = t0 + notes.length * 0.13;
  [2093, 1568.0].forEach((freq, i) => {
    const t = shimmerAt + i * 0.04;
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.09, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.0);
    audio.connectThrough(osc, g, master);
    osc.start(t);
    osc.stop(t + 1.05);
  });
}

/** Pause — a soft low two-note settle. */
function synthPause(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 392, type: 'sine' });
  osc.frequency.linearRampToValueAtTime(294, t + 0.18);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.01,
    decay: 0.05,
    sustain: 0.4,
    release: 0.22,
    peak: 0.12,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.42);
}

/** Resume — the same notes rising. */
function synthResume(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 294, type: 'sine' });
  osc.frequency.linearRampToValueAtTime(392, t + 0.16);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.01,
    decay: 0.05,
    sustain: 0.5,
    release: 0.2,
    peak: 0.13,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.4);
}

/** Soft transition whoosh for hub entry / nav. */
function synthWhoosh(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = 0.34;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 380, shaping: 1.4 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(600, t);
  bp.frequency.exponentialRampToValueAtTime(2000, t + dur);
  bp.Q.value = 0.6;
  const { node: g } = audio.envelope(ctx, {
    attack: 0.02,
    decay: 0.08,
    sustain: 0.6,
    release: dur,
    peak: 0.16,
  });
  audio.connectThrough(src, bp, g, master);
  src.start(t);
  src.stop(t + dur + 0.05);
}

// ---------------------------------------------------------------------------
// Public controller
// ---------------------------------------------------------------------------

class BingoSound {
  unlock(): void {
    audio.unlockAudio();
  }

  play(name: BingoSoundName): void {
    audio.playIfNotMuted(name, () => {
      audio.unlockAudio();
      switch (name) {
        case 'daub':
          synthDaub();
          return;
        case 'undaub':
          synthUndaub();
          return;
        case 'call':
          synthCall(false);
          return;
        case 'callLast':
          synthCall(true);
          return;
        case 'tap':
          synthTap();
          return;
        case 'nudge':
          synthNudge();
          return;
        case 'win':
          synthWin();
          return;
        case 'pause':
          synthPause();
          return;
        case 'resume':
          synthResume();
          return;
        case 'whoosh':
          synthWhoosh();
          return;
      }
    });
  }
}

export const sound = new BingoSound();
